import { Injectable } from '@angular/core';

import { Action, Store } from '@ngrx/store';
import { Actions, Effect, ofType } from '@ngrx/effects';

import { Selectors, AppState } from 'store/root-reducer';

import { Observable, of } from 'rxjs';
import { switchMap, catchError, map, exhaustMap, take, withLatestFrom } from 'rxjs/operators';

import { UserBill } from 'modules/shared/models/billable.models';
import { CheckBalancePayload } from 'store/models/billable.models';
import { SearchEngineTypeLabel } from 'modules/shared/models/screening.models';
import {
  NoCreditDialogData
} from 'modules/shared/components/no-credit-warning-dialog/no-credit-warning-dialog.component';

import {
  BillableActionTypes, LoadBillable, LoadBillableSuccess, LoadBillableFailure, CheckBalance, CheckBalanceSuccess, CheckBalanceFailure
} from 'store/actions/billable.actions';

import { BillableService } from 'modules/core/services/billable.service';
import {
  NoCreditWarningDialogService
} from 'modules/shared/components/no-credit-warning-dialog/no-credit-warning-dialog.service';

@Injectable()
export class BillableEffects {

  constructor(private store: Store<AppState>,
              private actions$: Actions,
              private billableService: BillableService,
              private noCreditWarningDialogService: NoCreditWarningDialogService) { }

  @Effect() CheckBalance: Observable<Action> = this.actions$.pipe(
    ofType<CheckBalance>(BillableActionTypes.CHECK_BALANCE),
    map((action: CheckBalance) => action.payload),
    withLatestFrom(this.store.select(Selectors.billable.getSubscribedButNoCreditEngines)),
    exhaustMap(([payload, noCreditSearchEngines]: [CheckBalancePayload, Array<SearchEngineTypeLabel | string>]) => {
      let showDialog: boolean = false;

      if (payload.searchEnginesToCheck && payload.searchEnginesToCheck.length) {
        showDialog = !!noCreditSearchEngines.filter((noCreditSearchEngine: SearchEngineTypeLabel | string) => {
          return payload.searchEnginesToCheck
            .map((searchEngineToCheck: string) => searchEngineToCheck.toLowerCase())
            .includes(noCreditSearchEngine.toLowerCase());
        }).length;
      } else {
        showDialog = !!noCreditSearchEngines.length;
      }

      if (showDialog) {
        return this.noCreditWarningDialogService
          .showNoCreditWarning(<NoCreditDialogData>{
            noCreditSearchEngines: noCreditSearchEngines,
            hideContinueButton: payload.searchEnginesToCheck && payload.searchEnginesToCheck.length === 1
          })
          .pipe(
            take(1),
            map((userDecision: 'close' | 'continue') => ({ userDecision: userDecision, actions: payload.actions }))
          );
      } else {
        return of({ userDecision: 'continue', actions: payload.actions });
      }
    }),
    switchMap((res: { userDecision: 'close' | 'continue', actions: Array<Action>}) => {
      if (res.userDecision === 'continue') {

        return [...res.actions, new CheckBalanceSuccess()];
      }

      return [{ type: 'EMPTY' }, , new CheckBalanceFailure()];
    })
  );

  @Effect() LoadBillable: Observable<Action> = this.actions$.pipe(
    ofType<LoadBillable>(BillableActionTypes.LOAD_BILLABLE),
    switchMap((action: LoadBillable) => {
      return this.billableService.getUserBillableInfo().pipe(
        map((bills: UserBill)  => new LoadBillableSuccess(bills)),
        catchError((error: any) => of(new LoadBillableFailure(error))),
      );
    })
  );
}
